import { HttpEventType, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, tap, throwError } from 'rxjs';
import { AuthService } from '../auth.service';
import { NetworkStatusService } from '../services/network-status.service';
import { captureClientError } from './error-capture';

export const AuthInterceptor: HttpInterceptorFn = (req, next) => {
    const router = inject(Router);
    const authService = inject(AuthService);
    const networkService = inject(NetworkStatusService);

    const token = sessionStorage.getItem('access_token');
    const sessionToken = sessionStorage.getItem('session_token');

    // Skip auth headers for external domains
    if (req.url.includes('api.ipify.org')) {
        return next(req);
    }

    let headers: Record<string, string> = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    if (sessionToken) headers['Session-Token'] = sessionToken;

    const clientIp = sessionStorage.getItem('client_ip');
    if (clientIp) headers['X-Client-IP'] = clientIp;

    const clientDeviceId = sessionStorage.getItem('client_device_id');
    if (clientDeviceId) headers['X-Device-Id'] = clientDeviceId;

    const userEmail = sessionStorage.getItem('user_email') || localStorage.getItem('user_email');
    if (userEmail) headers['X-User-Email'] = userEmail;

    // PRE-FLIGHT CIRCUIT BREAKER:
    // If the browser reports no network at all, cancel the request before it reaches the wire.
    // Only the browser's own signal counts here: the app's inferred offline state can be wrong
    // (one failed request, a probe that timed out), and blocking on it used to keep every API
    // call failing after the connection was back -- nothing could succeed to clear it.
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        return throwError(() => ({
            status: 0,
            statusText: 'Offline',
            userMessage: 'You are currently offline. Please check your internet connection.',
            isOffline: true
        }));
    }

    const clonedReq = req.clone({ setHeaders: headers });

    return next(clonedReq).pipe(
        tap((event) => {
            if (event.type === HttpEventType.Response) networkService.reportHttpSuccess();
        }),
        catchError((error) => {
            let errorMessage = 'Failed to fetch data. Please try again later.';

            if (error.status === 401) {
                // Token expired or invalid — logout immediately
                authService.logout();
                errorMessage = 'Your session has expired. Please log in again.';
            } else if (error.status === 0 || error.isOffline) {
                networkService.reportHttpNetworkError();
                errorMessage = 'You are currently offline. Please check your internet connection.';
            } else if (error.status === 502 || error.status === 504) {
                networkService.reportHttpNetworkError();
                errorMessage = 'Unable to reach WyberAI servers. Please check your network connection.';
            } else if (error.status === 503) {
                errorMessage = 'The service is temporarily unavailable. Please try again in a few moments.';
            } else if (error.status === 500) {
                const isLocalhost = typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
                const rawBody = typeof error?.error === 'string' ? error.error : JSON.stringify(error?.error || '');
                const errorStr = (rawBody + ' ' + (error?.message || '') + ' ' + (error?.statusText || '')).toLowerCase();
                const isProxyDisconnect = isLocalhost && (
                    (typeof navigator !== 'undefined' && !navigator.onLine) ||
                    errorStr.includes('enotfound') ||
                    errorStr.includes('getaddrinfo') ||
                    errorStr.includes('econnrefused') ||
                    errorStr.includes('etimedout') ||
                    errorStr.includes('econnreset') ||
                    errorStr.includes('ehostunreach') ||
                    errorStr.includes('enetunreach') ||
                    errorStr.includes('proxy error') ||
                    errorStr.includes('hpm')
                );

                if (isProxyDisconnect) {
                    networkService.reportHttpNetworkError();
                    errorMessage = 'You are currently offline. Please check your internet connection.';
                } else {
                    networkService.reportHttpNetworkError();
                    errorMessage = 'A server error occurred. Please contact support if the issue persists.';
                }
            } else if (error.status === 404) {
                errorMessage = 'The requested resource was not found. Please contact support if the issue persists.';
            } else if (error.status === 403) {
                errorMessage = 'You do not have permission to access this data. Please contact your administrator.';
            } else if (error?.error?.message || error?.message) {
                const customMessage = error?.error?.message || error?.message;
                errorMessage = typeof customMessage === 'string' ? customMessage : errorMessage;
            }

            // Capture the HTTP failure to the backend Error Logs feed.
            // status 0 / offline / proxy error = network drop → never log to backend (avoids telemetry storm).
            if (error.status !== 401 && error.status !== 403) {
                const isLocalhost = typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
                const rawBody = typeof error?.error === 'string' ? error.error : JSON.stringify(error?.error || '');
                const errorStr = (rawBody + ' ' + (error?.message || '') + ' ' + (error?.statusText || '')).toLowerCase();
                const isProxyDisconnect = isLocalhost && (
                    (typeof navigator !== 'undefined' && !navigator.onLine) ||
                    errorStr.includes('enotfound') ||
                    errorStr.includes('getaddrinfo') ||
                    errorStr.includes('econnrefused') ||
                    errorStr.includes('etimedout') ||
                    errorStr.includes('econnreset') ||
                    errorStr.includes('ehostunreach') ||
                    errorStr.includes('enetunreach') ||
                    errorStr.includes('proxy error') ||
                    errorStr.includes('hpm')
                );

                if (error.status === 0 || error.isOffline || isProxyDisconnect || networkService.isOffline || (typeof navigator !== 'undefined' && !navigator.onLine)) {
                    return throwError(() => ({ ...error, userMessage: errorMessage, isOffline: true }));
                }

                const category: 'API' | 'APPLICATION' = error.status === 0 ? 'APPLICATION' : 'API';
                const summary = `HTTP ${error.status || 0} on ${req.method} ${stripQuery(req.url)}`;
                let detailBody = '';
                try {
                    detailBody = typeof error.error === 'string'
                        ? error.error
                        : JSON.stringify(error.error);
                } catch {
                    detailBody = String(error.error);
                }
                const detail = `Message: ${error.message || errorMessage}. Body: ${detailBody}`;
                captureClientError({
                    category,
                    error: summary,
                    errorDetail: detail,
                    source: `${req.method} ${stripQuery(req.url)}`,
                });
            }

            return throwError(() => ({ ...error, userMessage: errorMessage }));
        }),
    );
};

/** Drops the query string from a URL so logged sources stay readable and don't leak tokens. */
function stripQuery(url: string): string {
    const idx = url.indexOf('?');
    return idx >= 0 ? url.slice(0, idx) : url;
}
